import { getTopologySelectList } from '@/api/quality-monitor';
import { Empty, useBreadcrumbRoutes, Wrapper, Radio, Select } from '@maxtropy/components';
import { Col, RadioChangeEvent, Row } from 'antd';
import React, { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProcessingEfficiency from './components/ProcessingEfficiency';
import ViewEnergyMediumList from './components/ViewEnergyMediumList';
import ViewEnergyMediumTopo from './components/ViewEnergyMediumTopo';
import styles from './index.module.scss';
import LossDuringTransAndDisProcess from './components/LossDuringTransAndDisProcess';
import { EnergyWorkingProcessType } from '@/api/uet';
import { V2EnergyCompareTopologyListPostResponse } from '@maxtropy/device-customer-apis-v2';

// 视图类型
export enum viewTypeEnum {
  CONFIGURATION = '1',
  LIST = '2',
}

export const viewTypeDisplay = {
  [viewTypeEnum.CONFIGURATION]: '组态视图',
  [viewTypeEnum.LIST]: '列表视图',
};

type EnergyCompareTopologyItem = Exclude<V2EnergyCompareTopologyListPostResponse['list'], undefined>[number];

const EnergyMediaTopologyAnalysis = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const urlProcessId = urlSearchParams.get('nodeId');
  const urlTopologyId = urlSearchParams.get('topologyId');
  const urlViewType = urlSearchParams.get('viewType');
  const [viewType, setViewType] = useState<viewTypeEnum>(() =>
    Object.values(viewTypeEnum).includes(urlViewType as viewTypeEnum)
      ? (urlViewType as viewTypeEnum)
      : viewTypeEnum.CONFIGURATION
  );
  const [energyMediumTopologyList, setEnergyMediumTopologyList] = useState<EnergyCompareTopologyItem[]>();
  const [topoId, setTopoId] = useState<number>(); // 选择的拓扑ID
  const [processId, setProcessId] = useState<Key>();
  const [selectedProcessType, setSelectedProcessType] = useState<EnergyWorkingProcessType>();
  const [selectedTopoSceneId, setSelectedTopoSceneId] = useState<number>();
  const [selectedProcessName, setSelectedProcessName] = useState<string>();

  const updateUrlSearchParams = useCallback(
    (updates: Record<string, string | number | undefined>) => {
      setUrlSearchParams(
        current => {
          const next = new URLSearchParams(current);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setUrlSearchParams]
  );

  // 查询拓扑列表
  useEffect(() => {
    getTopologySelectList().then(setEnergyMediumTopologyList);
  }, []);
  // topo列表options
  const topoListOptions = useMemo(() => {
    return (energyMediumTopologyList ?? []).map(i => ({ label: i.name, value: i.id }));
  }, [energyMediumTopologyList]);
  useEffect(() => {
    if (energyMediumTopologyList && energyMediumTopologyList.length > 0) {
      // 如果有 URL 参数中的 topologyId，优先使用它
      if (urlTopologyId) {
        const findTopo = energyMediumTopologyList.find(i => i.id === Number(urlTopologyId));
        if (findTopo) {
          setTopoId(findTopo.id);
          setSelectedTopoSceneId(findTopo.energyMediumSceneId);
        } else {
          setTopoId(energyMediumTopologyList[0].id);
          setSelectedTopoSceneId(energyMediumTopologyList[0].energyMediumSceneId);
          updateUrlSearchParams({ topologyId: energyMediumTopologyList[0].id, nodeId: undefined });
        }
      } else {
        setTopoId(energyMediumTopologyList[0].id);
        setSelectedTopoSceneId(energyMediumTopologyList[0].energyMediumSceneId);
        updateUrlSearchParams({ topologyId: energyMediumTopologyList[0].id });
      }
    }
  }, [energyMediumTopologyList, updateUrlSearchParams, urlTopologyId]);

  // 根据 URL 参数设置选中的 processId
  useEffect(() => {
    if (urlProcessId) {
      setProcessId(Number(urlProcessId));
    }
  }, [urlProcessId]);

  const onViewTypeChange = (e: RadioChangeEvent) => {
    const nextViewType = e.target.value as viewTypeEnum;
    setViewType(nextViewType);
    updateUrlSearchParams({ viewType: nextViewType });
  };

  const getProcessId = (key: Key) => {
    setProcessId(key);
    updateUrlSearchParams({ nodeId: String(key) });
  };
  const getProcessType = (type: EnergyWorkingProcessType) => {
    setSelectedProcessType(type);
  };

  const getProcessName = (name: string) => {
    setSelectedProcessName(name);
  };
  const handleTopoChange = (value: number) => {
    const findSceneIdById = energyMediumTopologyList?.find(i => i.id === value)?.energyMediumSceneId;
    findSceneIdById && setSelectedTopoSceneId(findSceneIdById);
    setTopoId(value);
    setProcessId(undefined);
    setSelectedProcessType(undefined);
    setSelectedProcessName(undefined);
    updateUrlSearchParams({ topologyId: value, nodeId: undefined });
  };
  return (
    <>
      <Wrapper routes={breadcrumbRoutes?.routes} className={styles.wrapper}>
        <div className={styles.contentBody}>
          <div className={styles.leftCol}>
            <div className={styles.leftColTopContent}>
              <Row style={{ width: '100%' }}>
                <Col span={14}>
                  <span>拓扑：</span>
                  <Select
                    style={{ width: '80%' }}
                    placeholder="请选择"
                    showSearch
                    optionFilterProp="label"
                    options={topoListOptions}
                    onChange={value => handleTopoChange(value)}
                    value={topoId}
                  />
                </Col>
                <Col span={10}>
                  <Radio.Group
                    options={Object.entries(viewTypeDisplay).map(([k, v]) => ({
                      label: v,
                      value: k,
                    }))}
                    onChange={e => {
                      onViewTypeChange(e);
                    }}
                    value={viewType}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Col>
              </Row>
            </div>
            {viewType === viewTypeEnum.CONFIGURATION ? (
              <div style={{ height: 'calc(100% - 46px)' }}>
                <ViewEnergyMediumTopo
                  energyMediumTopoId={topoId}
                  processIdRef={processId}
                  getProcessId={getProcessId}
                  getProcessType={getProcessType}
                  getProcessName={getProcessName}
                />
              </div>
            ) : (
              <div style={{ height: 'calc(100% - 46px)', overflowY: 'auto' }}>
                <ViewEnergyMediumList
                  energyMediumTopoId={topoId}
                  processIdRef={processId}
                  getProcessId={getProcessId}
                  getProcessType={getProcessType}
                  getProcessName={getProcessName}
                />
              </div>
            )}
          </div>
          <div className={styles.rightCol}>
            {!topoId ? (
              <Empty style={{ marginTop: '20vh' }} />
            ) : (
              <>
                {selectedProcessType === EnergyWorkingProcessType.PREPARATION_AND_HANDLE && (
                  <ProcessingEfficiency
                    topoId={topoId}
                    processId={processId}
                    selectedTopoSceneId={selectedTopoSceneId}
                    selectedProcessName={selectedProcessName}
                  />
                )}
                {selectedProcessType === EnergyWorkingProcessType.TRANSMISSION_AND_DISTRIBUTION && (
                  <LossDuringTransAndDisProcess
                    topoId={topoId}
                    processId={processId}
                    selectedTopoSceneId={selectedTopoSceneId}
                    selectedProcessName={selectedProcessName}
                  />
                )}
                {selectedProcessType === EnergyWorkingProcessType.STORAGE && <Empty style={{ marginTop: '20vh' }} />}
              </>
            )}
          </div>
        </div>
      </Wrapper>
    </>
  );
};

export default EnergyMediaTopologyAnalysis;
